// src/features/academic/components/QuizTimer.jsx
import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertCircle } from 'lucide-react';
import { formatTimeRemaining, getTimeColor } from '../utils/timeUtils';

/**
 * Quiz Timer Component
 * Displays a countdown timer for the current question
 */
const QuizTimer = ({ timeLeft, totalTime, onTimeUp }) => {
  const prevTimeRef = useRef(timeLeft);
  
  // Handle time up effect
  useEffect(() => {
    if (timeLeft === 0 && prevTimeRef.current > 0) {
      onTimeUp?.();
    }
    prevTimeRef.current = timeLeft;
  }, [timeLeft, onTimeUp]);
  
  // Calculate the percentage of time remaining
  const percentage = Math.max(0, Math.min(100, (timeLeft / totalTime) * 100));
  
  // Get color based on time remaining
  const timeColor = getTimeColor(timeLeft, totalTime);
  
  // Determine if timer should pulse when low
  const shouldPulse = timeLeft <= 5 && timeLeft > 0;
  
  return (
    <div className="relative">
      {/* Timer Display */}
      <motion.div
        animate={shouldPulse ? { scale: [1, 1.05, 1] } : {}}
        transition={{ repeat: shouldPulse ? Infinity : 0, duration: 0.5 }}
        className={`flex items-center ${timeColor} font-bold text-lg bg-white rounded-full px-3 py-1 shadow-sm border border-gray-100`}
        aria-live="polite"
      >
        <Clock className="mr-1.5" size={18} />
        <span>{formatTimeRemaining(timeLeft)}</span>
        
        {/* Low time warning */}
        {timeLeft <= 5 && timeLeft > 0 && (
          <AlertCircle className="ml-1.5 text-red-500" size={16} />
        )}
      </motion.div>
      
      {/* Progress Ring */}
      <svg 
        className="absolute top-0 left-0 w-full h-full -z-10"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        {/* Background ring */}
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="5"
        />
        
        {/* Progress ring */}
        <motion.circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke={timeLeft > 20 ? "#10B981" : timeLeft > 10 ? "#F59E0B" : "#EF4444"}
          strokeWidth="5"
          strokeDasharray="283"
          strokeDashoffset={283 - (percentage / 100) * 283}
          strokeLinecap="round"
          style={{
            transformOrigin: "center",
            transform: "rotate(-90deg)"
          }}
          animate={{ 
            strokeDashoffset: 283 - (percentage / 100) * 283 
          }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />
      </svg>
    </div>
  );
};

export default QuizTimer;